<cs-header>
  <cs-header-title>
    {{ LL.editWeighIn.title() }}
  </cs-header-title>
</cs-header>

<cs-content vld-progress [(vldStep)]="progressStep" [(vldTotalSteps)]="totalSteps">

  <cs-card-list vld-group>
    <!-- Future weigh-in info card -->
    @if (isDisabled) {
      <cs-info-card type="info"
        [message]="isCoachReadOnly ? LL.coach.weighInViewOnly() : LL.editWeighIn.info.futureWeighIn()"
        [dismissible]="false">
      </cs-info-card>
    }

    <!-- Weight warning card -->
    @if (showWeightWarning) {
      <cs-info-card type="warning"
        [message]="weightWarningMessage">
      </cs-info-card>
    }

    <!-- Body fat warning card -->
    @if (showBfWarning) {
      <cs-info-card type="warning" [message]="bfWarningMessage">
      </cs-info-card>
    }

    <!-- First time body fat measurement info card -->
    @if (showBodyFatFirstTimeInfo) {
      <cs-info-card type="info">
        {{ LL.editWeighIn.info.neverMeasuredBodyFat({frequency: bfFrequencyDays}) }}
        <help-link helpId="@help:references.body-composition">{{ LL.common.buttons.learnMore() }}</help-link>
      </cs-info-card>
    }

    <!-- Progress check needs a body-fat reading (#6067) -->
    @if (showProgressCheckNeedsBodyFat) {
      <cs-info-card type="info"
        [message]="LL.editWeighIn.info.bodyFatNeededForProgressCheck()">
      </cs-info-card>
    }

    <!-- Body fat cooldown period info card -->
    @if (showBodyFatCooldown) {
      <cs-info-card type="info"
        [message]="bfCooldownMessage">
      </cs-info-card>
    }
    <cs-card>
      <cs-card-title>
        {{ LL.editWeighIn.labels.bodyWeight() }}
        <cs-note>{{ LL.editWeighIn.notes.logWeightInstructions() }}</cs-note>
      </cs-card-title>
      <cs-card-content>
        <cs-item vertical>
          <cs-input-measure id="weightInput" type="bodyweight_kg" [(userUnit)]="this.weighIn.weight_unit"
            [(canonValue)]="this.weighIn.weight_kg" (canonValueChange)="somethingChanged()" [disabled]="isDisabled"
          [suggestedPreselectValue]="lastWeightValue" item-content input-sequence></cs-input-measure>
        </cs-item>
        <vld-message [validity]="validity('weight')" [immediate]="checkValues"></vld-message>
      </cs-card-content>
    </cs-card>

    <cs-card>
      <cs-card-title>
        {{ LL.editWeighIn.labels.bodyFat() }}
        <cs-note>{{ LL.editWeighIn.notes.bodyFatTracking() }}</cs-note>
      </cs-card-title>
      <cs-card-content>
        <cs-item-list>
          <cs-item-list-header>
            <cs-label>{{ LL.editWeighIn.labels.skinfolds() }}</cs-label>
            <cs-badge color="gray">{{ LL.common.ui.recommended() }}</cs-badge>
            @if (!calipersShown) {
              <cs-tooltip>
                {{ LL.editWeighIn.notes.measureSkinfolds() }}
                <br />
                <help-link helpId="@help:intake.bodyfat-caliper">
                  {{ LL.editWeighIn.links.howToMeasureSkinfolds() }}
                </help-link>
              </cs-tooltip>
            }
            <!-- [ariaLabel] names the switch: the tooltip beside it is a control of its own, so
                 there is no row text the toggle can take its name from -->
            <cs-toggle id="skinfoldsToggle" [ariaLabel]="LL.editWeighIn.labels.skinfolds()" [checked]="calipersShown" (checkedChange)="onBfSectionToggle('calipers', $event)" [disabled]="isDisabled"></cs-toggle>
            @if (calipersShown) {
              <cs-note>
                {{ LL.editWeighIn.notes.measureSkinfolds() }}
              </cs-note>
            }
            @if (calipersShown) {
              <help-link helpId="@help:intake.bodyfat-caliper">
                {{ LL.editWeighIn.links.howToMeasureSkinfolds() }}
              </help-link>
            }
          </cs-item-list-header>
          @if (calipersShown) {
            <div class="skinfold-grid">
              @for (skinfold of getSkinfolds(); track skinfold) {
                <cs-skinfold-site-measure [siteName]="skinfold.name"
                  [sitePhoto]="skinfold.photo" [(measures)]="weighIn.caliperMeasures[skinfold.id]" [readonly]="isDisabled"
                  [suggestedPreselectValue]="lastSkinfoldValues[skinfold.id]" (onValueChange)="somethingChanged()">
                </cs-skinfold-site-measure>
              }
            </div>
            <vld-message [validity]="validity('calipers')" [immediate]="checkValues"></vld-message>
          }
        </cs-item-list>
        <cs-content-divider></cs-content-divider>
        <cs-item-list>
          <cs-item-list-header>
            <cs-label>{{ LL.editWeighIn.labels.waist() }}</cs-label>
            @if (!waistShown) {
              <cs-tooltip>
                {{ LL.editWeighIn.notes.measureWaist() }}
                <help-link helpId="@help:references.body-composition">
                  {{ LL.common.buttons.learnMore() }}
                </help-link>
              </cs-tooltip>
            }
            <!-- [ariaLabel] names the switch: the tooltip beside it is a control of its own, so
                 there is no row text the toggle can take its name from -->
            <cs-toggle [ariaLabel]="LL.editWeighIn.labels.waist()" [checked]="waistShown" (checkedChange)="onBfSectionToggle('waist', $event)" [disabled]="isDisabled"></cs-toggle>
            @if (waistShown) {
              <cs-note>
                {{ LL.editWeighIn.notes.measureWaist() }}
              </cs-note>
            }
          </cs-item-list-header>
          @if (waistShown) {
            @for (i of [0]; track $index) {
              <cs-item vertical>
                <cs-input-measure type="bodydimensions_cm" [(userUnit)]="this.weighIn.tapeMeasures.unit"
                  [(canonValue)]="this.weighIn.tapeMeasures.waist_cm[i]" (canonValueChange)="somethingChanged()"
                  [suggestedPreselectValue]="lastWaistValue"
                  [disabled]="isDisabled" item-content input-sequence></cs-input-measure>
              </cs-item>
            }
            <vld-message [validity]="validity('waist')" [immediate]="checkValues"></vld-message>
          }
        </cs-item-list>
      </cs-card-content>
    </cs-card>
  </cs-card-list>

</cs-content>

@if (!isDisabled) {
  <cs-page-action-buttons>
    <cs-button medium [label]="LL.editWeighIn.buttons.save()" (click)="handleSaveButton($event)"></cs-button>
  </cs-page-action-buttons>
}